import React, { useState } from "react";
import { useSession } from "../lib/session";
import { createSociety, joinSociety } from "../lib/api";
import { Field, Notice } from "../components/ui";
import BrandShell from "../components/BrandShell";

/**
 * Shown when someone is signed in but belongs to no society. Two ways in:
 * start one, or join an existing one with the six-character code their
 * organiser hands out.
 */
export default function CreateSociety({ onBack }) {
  const { refresh, signOut, chooseSociety } = useSession();
  const [tab, setTab] = useState("join");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function go(e) {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const soc = tab === "join"
        ? await joinSociety(code)
        : await createSociety(name.trim());
      // open the one they've just joined or started, rather than bouncing
      // them back to a list with it on
      const list = await refresh();
      if (soc?.id) chooseSociety(soc.id, list);
    } catch (err) {
      const msg = err.message ?? String(err);
      setError(
        /function .*(create_society|join_society).* does not exist/i.test(msg)
          ? "The database is missing a migration. Run the files in db/ that you haven't yet, then try again."
          : msg);
      setBusy(false);
    }
  }

  return (
    <BrandShell>
      <form onSubmit={go}>
        <h1>{tab === "join" ? "Join your society" : "Set up your society"}</h1>
        <p>
          {tab === "join"
            ? "Your organiser will have given you a six-character code. It's usually on the fixture list or in the group chat."
            : "You'll be its admin. Members, courses and competitions all hang off this."}
        </p>

        <div className="tabs" style={{ marginBottom: "1.2rem" }}>
          <button type="button" data-on={tab === "join"} onClick={() => setTab("join")}>
            Join
          </button>
          <button type="button" data-on={tab === "create"} onClick={() => setTab("create")}>
            Start one
          </button>
        </div>

        {tab === "join" ? (
          <Field label="Society code">
            <input className="input code-input" required autoFocus maxLength={8}
                   autoCapitalize="characters" spellCheck="false"
                   value={code}
                   onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                   placeholder="W4YF9R" />
          </Field>
        ) : (
          <Field label="Society name"
                 hint="What your members call it. You can change this later.">
            <input className="input" required autoFocus value={name}
                   onChange={(e) => setName(e.target.value)}
                   placeholder="Wayfarers Golf Society" />
          </Field>
        )}

        <Notice kind="error">{error}</Notice>

        <button className="btn btn-primary btn-block"
                disabled={busy || (tab === "join" ? code.length < 4 : name.trim().length < 2)}
                style={{ marginTop: ".6rem" }}>
          {busy ? "Working…" : tab === "join" ? "Join society" : "Create society"}
        </button>

        <div style={{ marginTop: "1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
          {onBack && (
            <button type="button" className="btn-link" onClick={onBack}>
              Back to your societies
            </button>
          )}
          <button type="button" className="btn-link" style={{ color: onBack ? "var(--dim)" : undefined }}
                  onClick={signOut}>
            Sign out
          </button>
        </div>
      </form>
    </BrandShell>
  );
}
